import React from 'react';

import type { FormControlLabelProps, TextFieldProps } from '@mui/material';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  TextField,
} from '@mui/material';

import { Show } from '../../../components/Show';
import { ErrorMessageText } from './common';

type CheckboxGroupingProps = {
  name: string;
  options: CheckboxGroupingOption[];
  value: CheckboxGroupingValue;
  onChange: (value: CheckboxGroupingValue) => void;
  error?: string;
  disabled?: boolean;
};

export const CheckboxGrouping = (props: CheckboxGroupingProps) => {
  const optionValues = props.options.map((option) => option.value);

  const createCheckboxChangeHandler = (checkboxName: string | number) => {
    const handleChange: FormControlLabelProps['onChange'] = (e, checked) => {
      const optionIdx = optionValues.indexOf(checkboxName);

      const currentValues = props.value.values;

      // pad nextValues with placeholders
      const nextValues: CheckboxItemValue[] = optionValues.map(() => ({
        value: '',
      }));

      // add in current values
      currentValues.forEach((currentValue) => {
        const currentValueIdx = optionValues.indexOf(currentValue.value);
        if (currentValueIdx !== -1) {
          nextValues[currentValueIdx] = currentValue;
        }
      });

      nextValues[optionIdx] = checked ? { value: checkboxName } : { value: '' };

      props.onChange({ values: nextValues });
    };

    return handleChange;
  };

  const handleCommentChange: TextFieldProps['onChange'] = (e) => {
    props.onChange({ ...props.value, comment: e.target.value });
  };

  const isCommentFieldShown = useIsCommentFieldShown({
    options: props.options,
    value: props.value,
  });

  return (
    <FormGroup sx={{ paddingLeft: '8px' }}>
      {props.options.map((opt) => {
        const isOptionSelected = props.value.values.some(
          (val) => val.value == opt.value
        );

        return (
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={isOptionSelected}
                disabled={props.disabled}
              />
            }
            name={String(opt.value)}
            label={opt.label}
            onChange={createCheckboxChangeHandler(opt.value)}
          />
        );
      })}

      <Show when={isCommentFieldShown}>
        <InputLabel sx={{ fontSize: '1.1rem' }} required>
          Comments:
        </InputLabel>

        <TextField
          variant="standard"
          onChange={handleCommentChange}
          required
          multiline
          value={props.value.comment}
          disabled={props.disabled}
        />
      </Show>

      <ErrorMessageText>{props.error}</ErrorMessageText>
    </FormGroup>
  );
};

type CheckboxItemValue = { value: string | number };

type CheckboxGroupingValue = {
  values: CheckboxItemValue[];
  comment?: string;
};

type CheckboxGroupingOption = {
  label: string;
  value: CheckboxItemValue['value'];
  commentEligibility?: string;
};

type UseIsCommentFieldShownProps = {
  options: CheckboxGroupingOption[];
  value: CheckboxGroupingValue;
};

const useIsCommentFieldShown = (props: UseIsCommentFieldShownProps) => {
  return React.useMemo(() => {
    const optionValueCommentEligibility = props.options.reduce(
      (map, option) => ({
        ...map,
        [option.value]: Boolean(option.commentEligibility),
      }),
      {} as Record<string, boolean>
    );

    return props.value.values.reduce(
      (isShown, value) => isShown || optionValueCommentEligibility[value.value],
      false
    );
  }, [props.options, props.value.values]);
};
